import React from 'react';
import { Box, Container, Typography, Grid, CardMedia, Paper } from '@mui/material';
import withWebsiteContent from '../../../common/withWebsiteContent';
import { sectionScrollMarginSx } from '../../../common/hooks/useScrollSpyHash';

const StoryImage = ({ imageUrl, alt = 'Property image' }) => {
  if (imageUrl) {
    return (
      <CardMedia
        component="img"
        image={imageUrl}
        alt={alt}
        sx={{ height: { xs: 250, md: 350 }, objectFit: 'cover', borderRadius: 2 }}
      />
    );
  }
  return (
    <Box
      sx={{
        height: { xs: 250, md: 350 },
        backgroundColor: 'grey.200',
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed',
        borderColor: 'grey.400',
      }}
    >
      <Typography variant="body2" color="text.secondary">Image placeholder</Typography>
    </Box>
  );
};

const renderStory = (text) => {
  if (!text) return null;
  return text.split(/\n\n+/).map((paragraph, i) => (
    <Typography key={i} variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
      {paragraph}
    </Typography>
  ));
};

/**
 * Pure PropertyStory component — all content supplied via props.
 *
 * Expected website-component defaultProps shape (property-story.json):
 * { "title": "...", "story": "...", "importantInfoItems": ["...", "..."] }
 *
 * Images from the component's images/ folder in MinIO are injected as `images`.
 */
export const PropertyStoryPure = ({
  id,
  title = '',
  story = '',
  images = [],
  importantInfoItems = [],
}) => {
  if (!title && !story && importantInfoItems.length === 0) return null;

  return (
    <Box id={id} component="section" sx={{ ...sectionScrollMarginSx, py: 6, backgroundColor: 'white' }}>
      <Container>
        {title && (
          <Typography
            variant="h3"
            component="h2"
            sx={{ textAlign: 'center', mb: 5, fontWeight: 600, color: 'primary.main' }}
          >
            {title}
          </Typography>
        )}
        {(story || images.length > 0) && (
          <>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={story && images.length > 0 ? 6 : 12}>
                <Box sx={{ pr: { md: story && images.length > 0 ? 3 : 0 } }}>
                  {renderStory(story)}
                </Box>
              </Grid>
              {images.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    {images.slice(0, 2).map((url, i) => (
                      <Grid item xs={12} sm={images.length === 1 ? 12 : 6} key={url}>
                        <StoryImage imageUrl={url} alt={`Property view ${i + 1}`} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
            {images.length > 2 && (
              <Box sx={{ mt: 4 }}>
                <Grid container spacing={2}>
                  {images.slice(2).map((url, i) => (
                    <Grid item xs={12} sm={6} md={4} key={url}>
                      <StoryImage imageUrl={url} alt={`Property view ${i + 3}`} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
        {importantInfoItems.length > 0 && (
          <Paper
            elevation={0}
            sx={{ mt: 5, p: 3, backgroundColor: 'grey.50', borderLeft: '4px solid', borderColor: 'primary.main' }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Istotne informacje
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              {importantInfoItems.map((text, index) => (
                <Typography
                  key={index}
                  component="li"
                  variant="body1"
                  paragraph={index < importantInfoItems.length - 1}
                  sx={{ mb: index < importantInfoItems.length - 1 ? 1 : 0 }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

const PropertyStory = withWebsiteContent(PropertyStoryPure);
PropertyStory.displayName = 'PropertyStory';

export default PropertyStory;
